import { HiddenField } from '@/components/forms/fields/HiddenField';
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  FormField as ShadcnFormField,
} from '@/components/ui/form';
import { useFormContext } from 'react-hook-form';
import { BooleanField } from './fields/BooleanField';
import { DateField } from './fields/DateField';
import { ForeignKeyArrayField } from './fields/ForeignKeyArrayField';
import { ForeignKeyField } from './fields/ForeignKeyField';
import { RichTextField } from './fields/RichTextField';
import { SelectField } from './fields/SelectField';
import { TextareaField } from './fields/TextareaField';
import { TagsField } from './fields/TagsField';
import { TextField } from './fields/TextField';
import { ProcessedField } from './fields/types';
import { cn } from '@/lib/utils';

// A map from fieldType to the corresponding component.
const fieldMap = {
  hidden: HiddenField,
  text: TextField,
  richtext: RichTextField,
  select: SelectField,
  date: DateField,
  boolean: BooleanField,
  textarea: TextareaField,
  foreignKey: ForeignKeyField,
  foreignKeyArray: ForeignKeyArrayField,
  tags: TagsField, // FIXME: temp field for legacy lists
};

/** Field types that need items-start alignment instead of items-center */
const MULTILINE_TYPES = new Set(['richtext', 'textarea']);

/** Field types that use stacked layout (label above, control below) */
const STACKED_TYPES = new Set(['richtext', 'custom']);

interface FormFieldProps {
  field: ProcessedField;
  formMode: 'loading' | 'view' | 'edit';
}

export function FormField({ field, formMode }: FormFieldProps) {
  const { control } = useFormContext();

  // Check visibility configuration
  if (
    field.meta.fieldType === 'hidden' ||
    (field.meta.visibleIn && !field.meta.visibleIn.includes(formMode))
  ) {
    return null; // Field is not visible in current mode
  }

  // We handle the readOnly logic here, at the highest level.
  const effectiveFormMode =
    formMode === 'edit' && field.meta.readOnly ? 'view' : formMode;

  const fieldType = field.meta?.fieldType;
  const isMultiline = MULTILINE_TYPES.has(fieldType);
  const isStacked =
    field.meta.stackedLabel ??
    (STACKED_TYPES.has(fieldType) || !!field.meta.component);

  return (
    <div
      key={field.name}
      className={cn((field.meta.fullWidth || isStacked) && '@md:col-span-2')}
    >
      <ShadcnFormField
        control={control}
        name={field.name}
        render={({ field: controllerField }) => {
          const CustomComponent = field.meta?.component;
          const FieldComponent =
            CustomComponent || fieldMap[fieldType as keyof typeof fieldMap];

          if (!FieldComponent) {
            return (
              <div className="text-destructive bg-destructive/10 rounded-md p-2 text-sm">
                Unknown field definition for &quot;{field.name}&quot;
              </div>
            );
          }

          // Stacked layout: label above, control below (for custom components, richtext)
          if (isStacked) {
            const StackedIcon = field.meta.icon;
            return (
              <FormItem className="py-0.5">
                <FormLabel className="text-muted-foreground flex items-center gap-1.5 text-xs">
                  {StackedIcon && (
                    <StackedIcon className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                  )}
                  {field.meta.label}
                </FormLabel>
                <FormControl>
                  <FieldComponent
                    field={field}
                    formMode={effectiveFormMode}
                    controllerField={controllerField}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }

          // Inline layout: label and control on the same row
          const Icon = field.meta.icon;
          return (
            <FormItem>
              <div
                className={cn(
                  'flex w-full gap-2 py-0.5',
                  isMultiline ? 'items-start' : 'items-center',
                )}
              >
                {Icon && (
                  <Icon
                    className={cn(
                      'text-muted-foreground h-3.5 w-3.5 shrink-0',
                      isMultiline && 'mt-1.5',
                    )}
                  />
                )}
                <FormLabel
                  className={cn(
                    'text-muted-foreground w-16 shrink-0 text-xs',
                    isMultiline && 'mt-1.5',
                  )}
                >
                  {field.meta.label}
                </FormLabel>
                <FormControl>
                  <div className="min-w-0 flex-1">
                    <FieldComponent
                      field={field}
                      formMode={effectiveFormMode}
                      controllerField={controllerField}
                    />
                  </div>
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </div>
  );
}
